import React, { useState } from 'react';
import axios from 'axios';
import {
    Button,
    Form,
    Modal,
    Spinner,
    Toast,
    ToastContainer,
} from 'react-bootstrap';

import { ReportsData } from '../interface';
import { dataToCsv } from '../csvUtils';
import { globalURLS } from '../../../Global/urls.js';
import { useReportContext } from '../../../Context/index';

import './OfferScreenStatsModal.css';

interface Props {
    show: boolean;
    report: ReportsData | null;
    onHide: () => void;
}

function downloadCsv(filename: string, data: unknown, headers: string[]) {
    const csv = dataToCsv(data) || headers.join(',');
    const blob = new Blob(['\uFEFF' + csv], {
        type: 'text/csv;charset=utf-8',
    });
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
}

const OfferScreenStatsModal: React.FC<Props> = ({ show, report, onHide }) => {
    const { setErrorToastShow } = useReportContext();
    const [offerid, setOfferid] = useState('');
    const [downloading, setDownloading] = useState<'views' | 'clicks' | null>(
        null,
    );
    const [showDownloadOk, setShowDownloadOk] = useState(false);

    const viewsEndPoint =
        report?.offerScreenViewsEndPoint ?? 'ADgetOfferScreenViews';
    const clicksEndPoint =
        report?.offerScreenClicksEndPoint ?? 'ADgetOfferScreenClicks';

    const handleClose = () => {
        setOfferid('');
        setDownloading(null);
        setShowDownloadOk(false);
        onHide();
    };

    const handleDownload = async (kind: 'views' | 'clicks') => {
        const trimmed = offerid.trim();
        if (!trimmed || !report) return;

        setDownloading(kind);
        try {
            const endPoint = kind === 'views' ? viewsEndPoint : clicksEndPoint;
            const resp = await axios.post(
                `${globalURLS.baseURL}${endPoint}`,
                { offerid: trimmed },
                globalURLS.config,
            );
            if (resp?.data?.success === true && resp?.data?.data !== undefined) {
                const headers =
                    kind === 'views'
                        ? ['accountid', 'specialty', '#views']
                        : ['accountid', '#clicks'];
                downloadCsv(`${kind}_${trimmed}.csv`, resp.data.data, headers);
                setShowDownloadOk(true);
            } else {
                setErrorToastShow(true);
            }
        } catch {
            setErrorToastShow(true);
        } finally {
            setDownloading(null);
        }
    };

    const trimmed = offerid.trim();

    return (
        <>
            <ToastContainer
                position="top-end"
                className="p-3"
                style={{ zIndex: 1080 }}
            >
                <Toast
                    bg="success"
                    show={showDownloadOk}
                    onClose={() => setShowDownloadOk(false)}
                    delay={3000}
                    autohide
                >
                    <Toast.Header closeButton>
                        <strong className="me-auto">Download complete</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">
                        CSV saved to your downloads folder.
                    </Toast.Body>
                </Toast>
            </ToastContainer>
            <Modal
                show={show}
                onHide={handleClose}
                animation={false}
                centered
                dialogClassName="offer-screen-stats-modal-dialog"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="mb-0">
                        {report?.name ?? 'Offer Screen Stats'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-0">
                        <Form.Label htmlFor="offer-screen-offerid">
                            Offer ID
                        </Form.Label>
                        <Form.Control
                            id="offer-screen-offerid"
                            type="text"
                            placeholder="Enter offer ID"
                            value={offerid}
                            onChange={e => setOfferid(e.target.value)}
                            disabled={downloading !== null}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="offer-screen-stats-modal-footer">
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                    <div className="d-flex gap-2">
                        <Button
                            variant="primary"
                            type="button"
                            disabled={!trimmed || downloading !== null}
                            onClick={() => handleDownload('views')}
                        >
                            {downloading === 'views' ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Downloading…
                                </>
                            ) : (
                                'Download Views'
                            )}
                        </Button>
                        <Button
                            variant="success"
                            type="button"
                            disabled={!trimmed || downloading !== null}
                            onClick={() => handleDownload('clicks')}
                        >
                            {downloading === 'clicks' ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Downloading…
                                </>
                            ) : (
                                'Download Clicks'
                            )}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OfferScreenStatsModal;
